import styled, { css } from 'styled-components';

import Label from '../../../../_shared/components/Label';
import Button from '../../../../_shared/components/Button';
import Icon from '../../../../_shared/components/Icon';
import applyResponsiveVdpStyles from '../../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const StyledMultiMediaVariants = {
  mini: css`
    height: 245px;
    width: 100%;
  `,
  expanded: css`
    height: 213px;
    width: ${({ isSelling }) => (isSelling ? '285px' : '853px')};
    background-color: ${({ theme }) => theme.COLORS.BASE};

    ${({ isSelling }) =>
      isSelling ? '' : 'box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.2);'}
  `
};

const WrapperVariants = {
  mini: css`
    grid-template-columns: 1fr 1fr;
    width: 100%;
    display: flex;
  `,
  expanded: css`
    width: ${({ isSelling }) => (isSelling ? '285px' : '100%')};
    grid-template-columns: 285px repeat(4, [col] 142px);
    grid-template-rows: 1fr 1fr;
  `
};

const ImageContainerVariants = {
  mini: css`
    width: 100%;
    position: relative;
    max-height: 245px;
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0) 80%,
        ${({ theme }) => theme.COLORS.BASE_INVERTED} 86%
      );
    }
  `,
  glamourShot: css`
    grid-row: 1 / 3;
    height: 100%;
  `
};

const GalleryInfoVariants = {
  mini: css`
    display: flex;
    width: 100%;
    justify-content: space-around;
    align-items: center;
    bottom: 18px;
  `,
  expanded: css`
    bottom: 0;
    right: 0;
    z-index: 2;
    width: 284px;
    background: rgba(0, 0, 0, 0.7);
    height: 106px;
  `
};

const InfoWrapperVariants = {
  mini: css`
    width: 50%;
    position: absolute;
    left: -4px;
    bottom: -4px;
    padding: 0 25px;
  `,
  expanded: css`
    margin-top: 17px;
  `
};

const InfoVariants = {
  mini: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.XSMALL};
  `,
  expanded: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.XXSMALL};
  `
};

const MultiMediaButtonVariants = {
  mini: css`
    position: absolute;
    right: 13px;
    bottom: -4px;
    font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  `,
  expanded: css`
    margin: 10px auto;
  `
};

/**
 * Styled Components
 */
export const StyledMultiMedia = styled.div`
  display: flex;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledMultiMediaVariants.mini,
      expanded: StyledMultiMediaVariants.expanded
    })}
`;

export const Wrapper = styled.div`
  display: ${({ isSelling }) => (isSelling ? 'block' : 'grid')};
  position: relative;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: WrapperVariants.mini,
      expanded: WrapperVariants.expanded
    })}
`;

export const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: gray;
  max-height: 107px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: ImageContainerVariants.mini
    })}
`;

export const BigImageContainer = styled(ImageContainer)`
  grid-row: 1 / 3;
  ${({ onlyGlamourShot }) => (onlyGlamourShot ? 'height: 100%' : '')};
  position: relative;
  max-height: 213px;
`;

export const EmptyIcon = styled(Icon).attrs({ icon: 'TR5-No-image-camera' })`
  width: 26px;
  height: 22px;
`;

export const GalleryIcon = styled(Icon).attrs(props => ({ icon: props.icon }))`
  width: 14px;
  height: 12px;
  fill: ${({ theme }) => theme.COLORS.BASE};
`;

export const GalleryInfo = styled.div`
  position: absolute;
  color: ${({ theme }) => theme.COLORS.BASE};
  pointer-events: none;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: GalleryInfoVariants.mini,
      expanded: GalleryInfoVariants.expanded
    })}
`;

export const InfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 28px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: InfoWrapperVariants.mini,
      expanded: InfoWrapperVariants.expanded
    })}
`;

export const Info = styled.div`
  color: ${({ theme }) => theme.COLORS.BASE};
  align-self: flex-end;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: InfoVariants.mini,
      expanded: InfoVariants.expanded
    })}
`;

export const InfoNumber = styled(Label)`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  justify-content: center;
`;

export const GradientPattern = styled.div`
  position: absolute;
  width: 285px;
  height: 70px;
  margin-top: 143px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.0001) 0%,
    rgba(0, 0, 0, 0.8) 50%
  );
`;

export const StyledMultiMediaButton = styled(Button)`
  display: block;
  background-color: ${({ theme }) => theme.COLORS.TRANSPARENT};
  border: ${isOmp ? 2 : 1}px solid;
  width: 236px;
  color: ${({ theme }) => theme.COLORS.BASE};
  pointer-events: all;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: MultiMediaButtonVariants.mini,
      expanded: MultiMediaButtonVariants.expanded
    })};
  ${({ inBigImageContainer, theme }) =>
    inBigImageContainer &&
    `
      position: absolute;
      bottom: 0px;
      margin: 0px 30px 12px;
      border: 2px solid;
      font-size: ${theme.FONT_SIZES.SMALL};
      border-radius: 4px;
    `}
`;

export const Image = styled.img`
  width: 100%;
  height: 100%;
  cursor: ${props => (props.pointer ? 'pointer' : 'default')};
  object-fit: cover;
  object-position: center;
`;
